import { Link as RouterLink } from "react-router-dom";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Register } from "../../redux/actions/userActions";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import AuthShell from "../auth/AuthShell";
import Spinner from "../common/Spinner";

const RegisterPage = ({ history }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const dispatch = useDispatch();

  const [formState, setFormState] = useState({ values: {} });

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "name") {
      const regex = /^[a-zA-Z0-9\s]*$/;
      if (!regex.test(value)) return;
    }
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, [name]: value },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    const { name, email, password } = formState.values;
    if (!name || !email || !password || !acceptTerms) return;

    setLoading(true);
    dispatch(Register(name, email, password)).then((response) => {
      setLoading(false);
      if (response?.data?.token) {
        history.push(`/Verify/${response.data.token}`);
      }
    });
  };

  return (
    <>
      <AuthShell
        title="Create your account"
        subtitle="Sign up for Beta Appstore — private iOS & Android build distribution, in minutes."
        footer={
          <Typography variant="body2" align="center" sx={{ color: "text.secondary" }}>
            Already have an account?{" "}
            <Link component={RouterLink} to="/login" sx={{ fontWeight: 700 }}>
              Sign in
            </Link>
          </Typography>
        }
      >
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          <Stack spacing={2.5}>
            <TextField
              label="Name"
              name="name"
              required
              fullWidth
              autoFocus
              autoComplete="name"
              value={formState.values.name || ""}
              onChange={handleChange}
              error={submitted && !formState.values.name}
              helperText={submitted && !formState.values.name ? "Name is required" : " "}
            />

            <TextField
              label="Email"
              type="email"
              name="email"
              required
              fullWidth
              autoComplete="email"
              value={formState.values.email || ""}
              onChange={handleChange}
              error={submitted && !formState.values.email}
              helperText={submitted && !formState.values.email ? "Email is required" : " "}
            />

            <TextField
              label="Password"
              type={passwordVisible ? "text" : "password"}
              name="password"
              required
              fullWidth
              autoComplete="new-password"
              value={formState.values.password || ""}
              onChange={handleChange}
              error={submitted && !formState.values.password}
              helperText={submitted && !formState.values.password ? "Password is required" : " "}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordVisible((v) => !v)}
                      edge="end"
                      aria-label={passwordVisible ? "Hide password" : "Show password"}
                    >
                      {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  required
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  I accept the{" "}
                  <Link component={RouterLink} to="/terms-of-use" target="_blank">terms of use</Link>{" "}
                  and{" "}
                  <Link component={RouterLink} to="/privacy-policy" target="_blank">privacy policy</Link>.
                  <Box component="span" sx={{ color: "error.main", ml: 0.5 }} aria-hidden="true">*</Box>
                </Typography>
              }
            />
            {submitted && !acceptTerms && (
              <Typography variant="caption" color="error">
                You must accept the terms to continue.
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
            >
              {loading ? "Creating account…" : "Sign up"}
            </Button>
          </Stack>
        </form>
      </AuthShell>

      {loading && <Spinner fullscreen label="Creating your account…" />}
    </>
  );
};

export default RegisterPage;
